import {
  useInfiniteQuery,
  InfiniteData,
  UseInfiniteQueryOptions,
  keepPreviousData,
  useQuery,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
  useMutation,
} from '@tanstack/react-query';
import {
  api,
  TicketCountsDto,
  TicketDto,
  TicketListDto,
  UpdateTicketDto,
} from '@sib/api';
import { CACHE_TIME, DEFAULT_PAGE } from '@sib/api/constants';
import { QueryKey } from '@sib/api/enums';
import { InferSearchParamsQueryTypeWithArraySorter } from '@sib/api/types';

export const TICKET_NOTE_MAX_LENGTH = 4096;

export const getInfiniteTicketsListQueryKey = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.tickets.ticketsControllerGetTickets
  >
) => [
  QueryKey.Resource.Tickets,
  QueryKey.Operation.List,
  payload,
  QueryKey.Operation.Infinite,
];

export const useInfiniteTicketsList = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.tickets.ticketsControllerGetTickets
  >,
  options?: Partial<
    UseInfiniteQueryOptions<TicketListDto, Error, InfiniteData<TicketListDto>>
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: getInfiniteTicketsListQueryKey(payload),

    queryFn: async ({ pageParam }) => {
      const data = await api.tickets.ticketsControllerGetTickets({
        ...payload,
        page: pageParam as number,
      });

      return { ...data, newItems: 0 };
    },

    getNextPageParam: lastPage => {
      const { hasNextPage } = lastPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: payload?.page || DEFAULT_PAGE,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

export const useTicketsList = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.tickets.ticketsControllerGetTickets
  >,
  options?: Partial<UseQueryOptions<TicketListDto, Error, TicketListDto>>
) =>
  useQuery({
    ...options,
    queryKey: [QueryKey.Resource.Tickets, QueryKey.Operation.List, payload],
    queryFn: async () => api.tickets.ticketsControllerGetTickets(payload),
    placeholderData: keepPreviousData,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

type ApiTicketsCsvExportQuery = Parameters<
  typeof api.tickets.ticketsControllerExportTickets
>[0];

export type TicketsCsvExportQuery = Omit<
  ApiTicketsCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportTicketsCsv = async (query: TicketsCsvExportQuery) =>
  api.tickets.ticketsControllerExportTickets(query, {
    format: 'blob',
  }) as Promise<Blob>;

export const useExportTicketsCsv = (
  options?: Partial<UseMutationOptions<Blob, Error, TicketsCsvExportQuery>>
) =>
  useMutation({
    ...options,
    mutationFn: exportTicketsCsv,
  });

export const useGetOneTicket = (
  ticketId: string,
  options?: Partial<UseQueryOptions<TicketDto, Error, TicketDto>>
) =>
  useQuery({
    ...options,
    enabled: !!ticketId && (options?.enabled ?? true),
    queryKey: [QueryKey.Resource.Tickets, ticketId],
    queryFn: async () => api.tickets.ticketsControllerGetTicket(ticketId),
  });

export const useTicketCounts = (
  ticketId: string,
  options?: Partial<UseQueryOptions<TicketCountsDto, Error, TicketCountsDto>>
) =>
  useQuery({
    ...options,
    enabled: !!ticketId && (options?.enabled ?? true),
    queryKey: [QueryKey.Resource.Tickets, ticketId, QueryKey.Operation.Count],
    queryFn: async () => api.tickets.ticketsControllerGetTicketCounts(ticketId),
  });

export const useUpdateTicket = (
  options?: UseMutationOptions<
    TicketDto,
    Error,
    UpdateTicketDto & { ticketId: string }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Tickets, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Tickets, variables.ticketId],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.OrganizationTickets],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.ProjectTickets],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.VendorTickets],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.TicketOrganizationGroup],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TicketAssigneeGroup,
          QueryKey.Operation.List,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.TicketTypeGroup],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TicketFiles,
          variables.ticketId,
          QueryKey.Operation.List,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TicketHistory,
          variables.ticketId,
          QueryKey.Resource.Changelogs,
        ],
      });

      if (data.vendor?.id || variables.vendorId) {
        const vendorId = data.vendor?.id ?? variables.vendorId;
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.One,
            vendorId,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.List,
          ],
        });
      }

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({
      ticketId,
      ...payload
    }: UpdateTicketDto & { ticketId: string }) =>
      api.tickets.ticketsControllerUpdateTicket(ticketId, payload),
  });
};
